/**
 * EmergencyParticipantPage - Emergency participant management dashboard
 *
 * Surfaces the EmergencyParticipantDashboard from @ferp/workflow-forms for admin/architect users.
 * Safety-critical: manages participant status and communications during emergency events.
 *
 * Route: /admin/emergency-participants
 */

import { Container, Title, Group, Badge, Text, ThemeIcon, Alert, Stack } from '@mantine/core';
import { IconAlertTriangle, IconInfoCircle } from '@tabler/icons-react';
import { EmergencyParticipantDashboard } from '@ferp/workflow-forms';
import { useAuth } from '@/auth/AuthContext';

export function EmergencyParticipantPage() {
  const { tenant, user } = useAuth();

  // This component requires an active emergency context
  // In production, this would come from a route parameter or context
  const demoEmergencyId = 'demo-emergency-001';
  const demoEmergencyName = 'Facility Evacuation Drill';

  return (
    <Container size="xl" py="md">
      <Group justify="space-between" mb="lg">
        <Group gap="sm">
          <ThemeIcon size="lg" variant="light" color="red">
            <IconAlertTriangle size={20} />
          </ThemeIcon>
          <div>
            <Title order={3}>Emergency Participants</Title>
            <Text size="sm" c="dimmed">
              Safety-critical participant tracking during emergency events
            </Text>
          </div>
        </Group>
        {tenant && (
          <Badge variant="light" color="gray">
            {tenant.name}
          </Badge>
        )}
      </Group>

      <Stack gap="md">
        <Alert icon={<IconInfoCircle size={16} />} color="blue" variant="light">
          This dashboard shows participant status during active emergencies.
          Select an active emergency from the list below to view participants.
        </Alert>

        <EmergencyParticipantDashboard
          emergencyId={demoEmergencyId}
          emergencyName={demoEmergencyName}
          currentUserId={user?.id ?? 'unknown'}
          autoRegister={false}
        />
      </Stack>
    </Container>
  );
}

export default EmergencyParticipantPage;
